import { isCancelledPost, isLivePost, type PostDraft, type PostRecord, type PostShift } from "./model";

export type PostCounts = { posted: number; cancelled: number; notPosted: number; notConfirmed: number; total: number };

/** The roles the draft posts: each position with someone to hire, `"<shift key>/<position key>"`. */
export const draftEntries = (draft: PostDraft) =>
  draft.shifts.flatMap((shift) =>
    shift.positions.filter((position) => position.count > 0).map((position) => `${shift.key}/${position.key}`),
  );

/** One count, said the same everywhere (header, footer, receipt, chat): what posted, what did not, what may have. */
export const countOutcomes = (draft: PostDraft): PostCounts => {
  const entries = draftEntries(draft);
  const records = draft.records ?? {};
  const outcome = (entry: string): PostRecord["outcome"] | undefined => records[entry]?.outcome;
  // A role that went out, or may have, still counts if its card is gone: what is on the server does not depend on the draft.
  const gone = Object.entries(records).filter(
    ([entry, record]) =>
      !entries.includes(entry) && (record.outcome === "posted" || record.outcome === "not_confirmed"),
  );
  const goneOf = (kind: PostRecord["outcome"]) => gone.filter(([, record]) => record.outcome === kind).length;
  const sent = entries.filter((entry) => outcome(entry) === "posted").length + goneOf("posted");
  // A cancelled gig stays a posted record (it must never post again), but it is said as cancelled.
  const cancelled = Object.values(records).filter(isCancelledPost).length;
  return {
    posted: sent - cancelled,
    cancelled,
    notPosted: entries.filter((entry) => outcome(entry) !== "posted" && outcome(entry) !== "not_confirmed").length,
    notConfirmed: entries.filter((entry) => outcome(entry) === "not_confirmed").length + goneOf("not_confirmed"),
    total: entries.length + gone.length,
  };
};

/** Every role the post made live was cancelled since: nothing here is live, so nothing may say it is posted. */
export const allCancelled = ({ posted, cancelled, notConfirmed }: PostCounts) =>
  posted === 0 && notConfirmed === 0 && cancelled > 0;

/** What the chat hears instead of "posted" when every role it would touch was cancelled. */
export const CANCELLED_POST_MESSAGE =
  "This post's shifts were cancelled; nothing here is live. Post another starts a new one.";

/** Every role went out: what posted and what was cancelled since. The page shows the receipt, never an editable draft. */
export const allSent = ({ posted, cancelled, total }: PostCounts) => total > 0 && posted + cancelled === total;

/** The roles that went out, or may have, as `"<shift>/<position>"`. */
const sentEntries = (draft: PostDraft) =>
  Object.entries(draft.records ?? {})
    .filter(([, record]) => record.outcome === "posted" || record.outcome === "not_confirmed")
    .map(([entry]) => entry);

/** Whether a draft still holds the shift and position of every role that went out, or may have. */
export const keepsSentRoles = (draft: PostDraft) =>
  sentEntries(draft).every((entry) =>
    draft.shifts.some((shift) => shift.positions.some((position) => `${shift.key}/${position.key}` === entry)),
  );

/** Whether a role may have gone out and is not settled: its record settles a retry, so the draft is never replaced. */
export const anyUnconfirmed = (draft: PostDraft) =>
  Object.values(draft.records ?? {}).some((record) => record.outcome === "not_confirmed");

/** Whether any role of the draft went out, or may have. */
export const anySentRole = (draft: PostDraft) => sentEntries(draft).length > 0;

/** Whether the card, or the one role of it, has a role that went out or may have. */
export const hasSentRole = (draft: PostDraft, shiftKey: string, positionKey?: string) =>
  sentEntries(draft).some((entry) =>
    positionKey ? entry === `${shiftKey}/${positionKey}` : entry.startsWith(`${shiftKey}/`),
  );

/** The keys of a card's roles that went out, or may have: the roles its controls must not change. */
export const sentRoleKeys = (draft: PostDraft, shiftKey: string) =>
  new Set(
    sentEntries(draft)
      .filter((entry) => entry.startsWith(`${shiftKey}/`))
      .map((entry) => entry.slice(shiftKey.length + 1)),
  );

/** Equal by value, whatever the order the keys were written in. */
const canonical = (value: unknown): string =>
  JSON.stringify(value, (_key, each: unknown) =>
    each && typeof each === "object" && !Array.isArray(each)
      ? Object.fromEntries(Object.entries(each).toSorted(([left], [right]) => left.localeCompare(right)))
      : each,
  );

/** What a card says about the day, time, place and manager: everything but its roles and what the page keeps about it. */
const cardFields = ({
  positions: _positions,
  touched: _touched,
  hand_set: _handSet,
  defaults: _defaults,
  ...fields
}: PostShift) => canonical(fields);

/** The card whose sent role the change touches, `""` for the PO number (it rides on every role), else null. */
const lockedCardOf = (before: PostDraft, next: PostDraft) => {
  // The PO number rides on every create: once one role went out, a new one would split the shifts of one post.
  if (anySentRole(before) && (before.po_number ?? "") !== (next.po_number ?? "")) return "";
  const locked = next.shifts.find((shift) => {
    const was = before.shifts.find(({ key }) => key === shift.key);
    if (!was) return false;
    const kept = [...sentRoleKeys(before, shift.key)].filter((key) => was.positions.some((each) => each.key === key));
    if (kept.length === 0) return false;
    if (cardFields(was) !== cardFields(shift)) return true;
    return kept.some((key) => {
      const after = shift.positions.find((each) => each.key === key);
      return !!after && canonical(after) !== canonical(was.positions.find((each) => each.key === key));
    });
  });
  return locked ? locked.key : null;
};

/**
 * Whether going from one draft to another changes a role that went out, or may have, or the day, time, place or manager of a
 * card that has one. Its gig is live: the change would never reach it, and a Retry would record the old gig as the new
 * content. Adding a role, or removing one (which says so and does not cancel the gig), is not a change to it.
 */
export const changesSentRoles = (before: PostDraft, next: PostDraft) => lockedCardOf(before, next) !== null;

/** What to tell the manager when they remove roles that already posted: removing them does not cancel the gigs. */
export const postedRemovalNotice = (draft: PostDraft, shiftKey: string, positionKeys?: ReadonlyArray<string>) => {
  const shift = draft.shifts.find(({ key }) => key === shiftKey);
  const names = (shift?.positions ?? [])
    .filter((position) => !positionKeys || positionKeys.includes(position.key))
    .filter((position) => isLivePost(draft.records?.[`${shiftKey}/${position.key}`]))
    .map((position) => position.position_label ?? "A position");
  if (names.length === 0) return null;
  return `${names.join(", ")} ${names.length === 1 ? "is" : "are"} already posted. Removing ${names.length === 1 ? "it" : "them"} here doesn't cancel the gig. Cancel it from the Schedule.`;
};

/** Why a role or card that has gone out cannot be changed here: its gig is live, so the change would not reach it. */
export const SENT_LOCK_MESSAGE =
  "That has already posted, or may have, so changing it here would not change the live gig. Change it from the Schedule.";

/** The lock's words for one card (or the whole draft): a card whose every sent role was cancelled has nothing live. */
export const sentLockMessage = (draft: PostDraft, shiftKey?: string) => {
  const sent = sentEntries(draft).filter((entry) => !shiftKey || entry.startsWith(`${shiftKey}/`));
  return sent.length > 0 && sent.every((entry) => isCancelledPost(draft.records?.[entry]))
    ? CANCELLED_POST_MESSAGE
    : SENT_LOCK_MESSAGE;
};

/** Why going from one draft to the other is refused, in the words of the card it touches; null when it is not. */
export const sentLockOf = (before: PostDraft, next: PostDraft) => {
  const card = lockedCardOf(before, next);
  return card === null ? null : sentLockMessage(before, card || undefined);
};

export const countLine = ({ posted, cancelled, notPosted, notConfirmed }: PostCounts) =>
  [
    `${posted} posted`,
    cancelled > 0 ? `${cancelled} cancelled` : null,
    notConfirmed > 0 ? `${notConfirmed} not confirmed` : null,
    notPosted > 0 ? `${notPosted} not posted` : null,
  ]
    .filter(Boolean)
    .join(", ");

/** One role a post made live, as the chat is told it: its gig and what its card held when it posted. */
export type PostedLine = {
  gig_id: number;
  card_id: string;
  role_id: string;
  position: string;
  count: number;
  day: string;
  start: string;
  end: string;
};

/** The roles THIS post made live and that are still live: never an earlier run's, a hand Post's, or a cancelled one. */
export const postedLines = (
  shifts: ReadonlyArray<PostShift>,
  before: Readonly<Record<string, PostRecord>>,
  now: Readonly<Record<string, PostRecord>>,
): Array<PostedLine> =>
  shifts.flatMap((shift) =>
    shift.positions.flatMap((position) => {
      const entry = `${shift.key}/${position.key}`;
      const record = now[entry];
      if (!isLivePost(record) || before[entry]?.outcome === "posted" || record?.gig_id == null) return [];
      return [
        {
          gig_id: record.gig_id,
          card_id: shift.key,
          role_id: position.key,
          position: position.position_label ?? "Position",
          count: position.count,
          day: shift.date ?? "",
          start: shift.start_time ?? "",
          end: shift.end_time ?? "",
        },
      ];
    }),
  );

/**
 * What the chat is told when a post stopped part way, one line each: what this run posted ("Posted Line Cook · Fri, Oct
 * 30."), the role it stopped on ("Not posted: Dishwasher · <reason>", or "Not confirmed: …" when it may have posted), and
 * the roles it never reached ("Not tried: Busser."). With one card the day is said once; with several, each role has its own.
 */
export const stoppedLines = (
  shifts: ReadonlyArray<PostShift>,
  before: Readonly<Record<string, PostRecord>>,
  now: Readonly<Record<string, PostRecord>>,
  stopped: { entry: string; message: string },
  dayOf: (date: string) => string,
): { lines: Array<string>; notConfirmed: string | null } => {
  const several = shifts.length > 1;
  const roles = shifts.flatMap((shift) =>
    shift.positions
      .filter((position) => position.count > 0)
      .map((position) => {
        const day = shift.date ? dayOf(shift.date) : null;
        const name = position.position_label ?? "Position";
        return { shift, entry: `${shift.key}/${position.key}`, name, said: several && day ? `${name} · ${day}` : name };
      }),
  );
  const at = roles.findIndex(({ entry }) => entry === stopped.entry);
  const role = roles[at];
  const posted = postedLines(shifts, before, now);
  const postedLine = shifts.flatMap((shift) => {
    const names = posted.filter(({ card_id }) => card_id === shift.key).map(({ position }) => position);
    const day = shift.date ? ` · ${dayOf(shift.date)}` : "";
    return names.length > 0 ? [`Posted ${names.join(", ")}${day}.`] : [];
  });
  const notConfirmed =
    role && now[role.entry]?.outcome === "not_confirmed"
      ? `Not confirmed: ${role.name} may have posted. Check the Schedule before posting again.`
      : null;
  // Nothing went out: the reason alone, as before.
  if (posted.length === 0) return { lines: [notConfirmed ?? `Not posted: ${stopped.message}`], notConfirmed };
  const refused =
    notConfirmed ?? (role ? `Not posted: ${role.said} · ${stopped.message}` : `Not posted: ${stopped.message}`);
  const untried = at === -1 ? [] : roles.slice(at + 1).filter(({ entry }) => !now[entry]);
  return {
    lines: [
      ...postedLine,
      refused,
      ...(untried.length > 0 ? [`Not tried: ${untried.map(({ said }) => said).join(", ")}.`] : []),
    ],
    notConfirmed,
  };
};
